import { stepAsteroidField } from "./ring";
import { crewMannedCount, stepCrew } from "./crew";
import { stepDocking } from "./docking";
import { getItemDef } from "./items";
import { isModuleDisabled, isShipPowered } from "./module-disable";
import { cellKeyNum, getNetworks, markModulesDirty, markNetworksDirty, type ResourceNetwork } from "./networks";
import { isFar } from "./lod";
import { jitterAngle } from "./physics/config";
import { physicsStep } from "./physics/step";
import { applyGravity } from "./planet";
import { stepPlumes } from "./plume";
import {
  buildShipGrid,
  forEachCellAlongLocalRay,
  forEachShipAlongRay,
  releaseShipGrid,
  type ShipGrid,
} from "./ship-grid";
import { emitSparks } from "./sparks";
import {
  addEnergyToNetwork,
  cardinalVec,
  drawCargoFromNetwork,
  drawEnergyFromNetwork,
  drawFuelFromNetwork,
  effectiveCells,
  moduleLocalCenter,
  moduleWorldCenter,
  recomputeShipMassAndRebase,
  rotateCardinal,
  shipCellSize,
  shipExtent,
  splitDisconnectedComponents,
  thrusterPortBlocked,
} from "./ship";
import {
  AMBIENT_K,
  itemRadiateCoefW,
  moduleActiveWasteHeatW,
  radiativeCool,
  stepShipThermal,
  thermalCap,
} from "./thermal";
import { clamp } from "../util/math";
import { spawnItem } from "./world";
import type { DamageCause, ModuleDef, Projectile, Ship, ShipModule, SpaceItem, World } from "./types";

const RUINED_QUALITY = 0.01;

export const TICK_DT = 1 / 120;

/** Thermal runs every other tick at 2× dt — see stepWorld. (Sparks and rigid
 *  bodies integrate every tick; only thermal is half-rate.) */
const HALF_RATE_DT = TICK_DT * 2;

/** Beams fire every 120 Hz tick; ETA_EJECT would firehose sparks. */
const ETA_BEAM = 0.01;

const BOOT_COOLDOWN_S = 2;
const BOOT_COOLDOWN_JITTER_S = 5;
const BATTERY_FILL_TARGET_S = 20;

const DEFAULT_AMMO_REFILL_S = 0.5;

export interface SimViewport {
  cx: number;
  cy: number;
  halfW: number;
  halfH: number;
  /** Active-zone centre (the player ship). Bodies beyond ACTIVE_RADIUS_M of
   *  this point are demoted to gravity-only point bodies — see lod.ts. */
  focusX: number;
  focusY: number;
}

function shipInViewport(ship: Ship, vp: SimViewport): boolean {
  const ext = shipExtent(ship);
  const dx = ship.body.pos.x - vp.cx;
  const dy = ship.body.pos.y - vp.cy;
  return Math.abs(dx) <= vp.halfW + ext && Math.abs(dy) <= vp.halfH + ext;
}

function itemInViewport(it: SpaceItem, vp: SimViewport): boolean {
  return Math.abs(it.pos.x - vp.cx) <= vp.halfW && Math.abs(it.pos.y - vp.cy) <= vp.halfH;
}

const _activeDrawByNet = new Map<ResourceNetwork, number>();
const _fuelBurnByNet = new Map<ResourceNetwork, number>();
const _variableBudgetByNet = new Map<ResourceNetwork, number>();
let _activeHeatByModule: Float64Array = new Float64Array(64);
let _desiredActivation: Float64Array = new Float64Array(8);
const _beamVisitedShips = new Set<Ship>();
const _beamVisitedModules = new Set<ShipModule>();

function ensureHeatBuf(n: number): void {
  if (_activeHeatByModule.length < n) _activeHeatByModule = new Float64Array(n);
  for (let i = 0; i < n; i++) _activeHeatByModule[i] = 0;
}

function ensureDesiredBuf(n: number): void {
  if (_desiredActivation.length < n) _desiredActivation = new Float64Array(n);
}

export function stepWorld(world: World, viewport?: SimViewport): void {
  world.beams.length = 0;
  const dirty = _dirtySet;
  dirty.clear();

  stepCrew(world);

  // Hydrate asteroids entering the active zone / dehydrate those leaving, and
  // orbit-integrate the dormant proxies. Runs before the ship loop so a freshly
  // hydrated body is fully simulated this very tick.
  stepAsteroidField(
    world, TICK_DT,
    viewport?.focusX ?? 0, viewport?.focusY ?? 0, viewport != null,
  );

  const halfRateTick = (world.tick & 1) === 0;

  for (const ship of world.ships.values()) {
    const thermalActive = !viewport || shipInViewport(ship, viewport);
    stepShip(ship, world, thermalActive, halfRateTick, dirty);
  }

  for (const ship of world.ships.values()) {
    if (!ship.fire1) continue;
    const inMiningMode = ship.fireMode === "mining";
    for (const m of ship.modules) {
      if (m.hp <= 0 || m.disabled || m.manned === false) continue;
      const def = m.def;
      const w = def.weapon;
      if (!w) continue;
      if ((w.altFire === true) !== inMiningMode) continue;
      if (w.kind === "beam") continue;
      const cooldown = w.rof > 0 ? 1 / w.rof : Infinity;
      if (world.time < m.lastFiredAt + cooldown) continue;
      if (w.ammoMax != null && (m.ammo ?? 0) <= 0) continue;
      fireWeapon(world, ship, m, def.cells, w);
      m.lastFiredAt = world.time;
      if (w.ammoMax != null && m.ammo != null) m.ammo -= 1;
    }
  }

  // Planet gravity is a velocity force, applied after thrust and before the
  // integrator consumes body.vel.
  applyGravity(world, TICK_DT);

  // Only active-zone bodies enter the collision solver; far bodies are
  // integrated cheaply below (gravity already updated their velocity).
  physicsStep(world, TICK_DT, dirty, viewport);
  integrateFarBodies(world, viewport, TICK_DT);

  // Beams after physics: muzzle/raycast/quad read post-integration pose.
  const shipGrid = buildShipGrid(world);
  for (const ship of world.ships.values()) {
    if (!ship.fire1) continue;
    const inMiningMode = ship.fireMode === "mining";
    for (const m of ship.modules) {
      if (m.hp <= 0 || m.disabled || m.manned === false) continue;
      const def = m.def;
      const w = def.weapon;
      if (!w || w.kind !== "beam") continue;
      if ((w.altFire === true) !== inMiningMode) continue;
      fireBeam(world, ship, m, def, w, dirty, shipGrid);
    }
  }
  releaseShipGrid(shipGrid);

  if (halfRateTick) {
    for (const it of world.items.values()) {
      if (viewport && !itemInViewport(it, viewport)) continue;
      if (it.tempK <= AMBIENT_K) continue;
      const def = getItemDef(it.defId);
      const cap = Math.max(1, def.stats.mass) * def.stats.thermalInertia;
      it.tempK = radiativeCool(it.tempK, cap, itemRadiateCoefW(def), HALF_RATE_DT);
    }
    for (const it of world.items.values()) {
      if (it.tempK < AMBIENT_K) it.tempK = AMBIENT_K;
    }
  }

  stepDocking(world, TICK_DT);

  for (const ship of dirty) cleanupShipAfterDamage(world, ship);

  // After damage cleanup: plumes skip modules whose host just died.
  stepPlumes(world, TICK_DT);

  world.tick++;
  world.time += TICK_DT;
}

const _dirtySet = new Set<Ship>();

/** Advances bodies outside the active zone. physicsStep skips them entirely
 *  (no handle, no collision), so their position/orientation would otherwise
 *  freeze; here we apply the same explicit-Euler step the solver gives near
 *  bodies, keeping distant orbits ticking. Velocity was already updated by
 *  applyGravity, so an orbiting asteroid coasts identically whether near or
 *  far — it just stops colliding until the player returns. */
function integrateFarBodies(world: World, vp: SimViewport | undefined, dt: number): void {
  if (!vp) return;
  for (const ship of world.ships.values()) {
    if (!isFar(ship.body.pos.x, ship.body.pos.y, vp.focusX, vp.focusY)) continue;
    ship.body.pos.x += ship.body.vel.x * dt;
    ship.body.pos.y += ship.body.vel.y * dt;
    ship.body.rot += ship.body.angVel * dt;
  }
  for (const it of world.items.values()) {
    if (!isFar(it.pos.x, it.pos.y, vp.focusX, vp.focusY)) continue;
    it.pos.x += it.vel.x * dt;
    it.pos.y += it.vel.y * dt;
    it.rot += it.angVel * dt;
  }
}

function stepShip(ship: Ship, world: World, thermalActive: boolean, runThermal: boolean, dirty: Set<Ship>): void {
  const dt = TICK_DT;
  if (ship.isAsteroid) return;
  // Boarded ships dragged kinematically; own actuation would fight the snap.
  const passive = ship.boardedByShipId != null;

  let reqFwd  = passive ? 0 : clamp(ship.throttle, -1, 1);
  let reqLat  = passive ? 0 : clamp(ship.strafe, -1, 1);
  let reqSpin = passive ? 0 : clamp(ship.steer, -1, 1);

  if (!passive && ship.autoBrake) {
    const rot = jitterAngle(-ship.body.rot);
    const cs = Math.cos(rot);
    const sn = Math.sin(rot);
    const localVx = ship.body.vel.x * cs - ship.body.vel.y * sn;
    const localVy = ship.body.vel.x * sn + ship.body.vel.y * cs;
    if (Math.abs(reqFwd)  < 0.05) reqFwd  = clamp(-localVx / 8, -1, 1);
    if (Math.abs(reqLat)  < 0.05) reqLat  = clamp(localVy / 6, -1, 1);
    if (Math.abs(reqSpin) < 0.05) reqSpin = clamp(-ship.body.angVel / 0.6, -1, 1);
  }

  const nets = getNetworks(ship);

  for (let mi = 0; mi < ship.modules.length; mi++) {
    const m = ship.modules[mi];
    const stats = m.def.stats;
    if (stats.crew === 0) { m.manned = true; continue; }
    if (m.hp <= 0) { m.manned = false; continue; }
    m.manned = crewMannedCount(world, ship, mi) >= stats.crew;
  }

  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    const def = m.def;
    if (def.category !== "reactor" || def.stats.powerOutW <= 0) continue;
    if (!def.reactor || def.reactor.fuelJoulesPerKg <= 0) continue;
    if (m.disabled || m.manned === false) continue;
    if (m.fuelBuffer == null) m.fuelBuffer = 0;
    const buffRoom = def.reactor.fuelBufferKg - m.fuelBuffer;
    if (buffRoom > 0) {
      const net = nets.networkOf.get(m);
      if (net) m.fuelBuffer += drawCargoFromNetwork(net, def.reactor.fuelResource, buffRoom);
    }
  }

  // Settle before per-module isModuleDisabled (which reads ship.powered).
  ship.powered = isShipPowered(world, ship);

  for (const m of ship.modules) {
    m.disabled = isModuleDisabled(world, ship, m);
    m.powered = !m.disabled;
  }

  for (const net of nets.networks) {
    let capacity = 0;
    let used = 0;
    for (const m of net.modules) {
      if (m.disabled) continue;
      const def = m.def;
      if (def.category === "reactor") capacity += def.stats.powerOutW;
      else used += def.stats.powerIdleW;
    }
    for (const m of net.modules) {
      if (m.awaitingPower !== true || m.hp <= 0) continue;
      const def = m.def;
      if (def.category === "reactor") {
        m.awaitingPower = undefined;
        m.rebootAt = world.time + BOOT_COOLDOWN_S
          + Math.random() * BOOT_COOLDOWN_JITTER_S;
        continue;
      }
      const idle = def.stats.powerIdleW;
      if (used + idle <= capacity) {
        used += idle;
        m.awaitingPower = undefined;
        m.rebootAt = world.time + BOOT_COOLDOWN_S
          + Math.random() * BOOT_COOLDOWN_JITTER_S;
      }
    }
  }
  for (const net of nets.networks) {
    let anyOn = false;
    for (const m of net.modules) if (!m.disabled) { anyOn = true; break; }
    net.powered = anyOn;
  }

  let totalFx = 0, totalFy = 0, totalTau = 0;
  _activeDrawByNet.clear();
  _fuelBurnByNet.clear();
  ensureHeatBuf(ship.modules.length);
  const inMining = ship.fireMode === "mining";

  // Pass 1 (binary-draw): thrusters deferred so they scale to remaining budget.
  for (let mi = 0; mi < ship.modules.length; mi++) {
    const m = ship.modules[mi];
    if (m.hp <= 0 || m.disabled || m.manned === false) continue;
    const def = m.def;
    if (def.thrusters) continue;
    const stats = def.stats;
    const net = nets.networkOf.get(m);
    const myPowered = m.powered ?? false;
    const weaponsActive = myPowered && ship.fire1;

    let categoryActive = false;
    switch (def.category) {
      case "weapon": {
        const w = def.weapon;
        const familyMatch = weaponsActive && w != null
          && (w.altFire === true) === inMining;
        const hasAmmo = !w || w.ammoMax == null || (m.ammo ?? 0) > 0;
        categoryActive = familyMatch && hasAmmo;
        break;
      }
      case "life_support":
      case "cockpit":
        categoryActive = myPowered;
        break;
      default: break;
    }
    if (categoryActive) {
      if (net) _activeDrawByNet.set(net, (_activeDrawByNet.get(net) ?? 0) + stats.powerActiveW);
      _activeHeatByModule[mi] += moduleActiveWasteHeatW(def);
    }
  }

  _variableBudgetByNet.clear();
  for (const net of nets.networks) {
    let reactorNet = 0;
    let consumerIdle = 0;
    let stored = 0;
    for (const mm of net.modules) {
      if (mm.hp <= 0 || mm.disabled) continue;
      const ddef = mm.def;
      if (ddef.category === "reactor") {
        reactorNet += ddef.stats.powerOutW - ddef.stats.powerIdleW;
      } else {
        consumerIdle += ddef.stats.powerIdleW;
      }
      stored += mm.energyWh ?? 0;
    }
    const activeUsed = _activeDrawByNet.get(net) ?? 0;
    const reactorSurplus = Math.max(0, reactorNet - consumerIdle - activeUsed);
    const batteryThisTick = (stored * 3600) / dt;
    _variableBudgetByNet.set(net, reactorSurplus + batteryThisTick);
  }

  for (let mi = 0; mi < ship.modules.length; mi++) {
    const m = ship.modules[mi];
    if (m.hp <= 0 || m.disabled || m.manned === false) continue;
    const def = m.def;
    if (!def.thrusters) continue;
    const net = nets.networkOf.get(m);
    const myPowered = m.powered ?? false;
    let netHasFuel = false;
    if (net) {
      for (const mm of net.modules) {
        if (mm.hp > 0 && (mm.fuelKg ?? 0) > 0) { netHasFuel = true; break; }
      }
    }
    if (!myPowered || !netHasFuel || !net) {
      m.thrusterActivations.fill(0);
      continue;
    }
    const r = moduleLocalCenter(ship, m);
    const eff = effectiveCells(def, m.facing);
    const tCount = def.thrusters.length;
    ensureDesiredBuf(tCount);
    let wantedW = 0;
    for (let i = 0; i < tCount; i++) {
      const port = def.thrusters[i];
      const shipDir = cardinalVec(rotateCardinal(port.dir, m.facing));
      const Fx = shipDir.x * port.thrustN;
      const Fy = shipDir.y * port.thrustN;
      const tau = r.x * Fy - r.y * Fx;
      const tauSign = tau > 0 ? 1 : tau < 0 ? -1 : 0;
      let a = 0;
      if (reqFwd  > 0 && shipDir.x > 0) a += reqFwd;
      if (reqFwd  < 0 && shipDir.x < 0) a += -reqFwd;
      if (reqLat  > 0 && shipDir.y < 0) a += reqLat;
      if (reqLat  < 0 && shipDir.y > 0) a += -reqLat;
      if (reqSpin > 0 && tauSign > 0)   a += reqSpin;
      if (reqSpin < 0 && tauSign < 0)   a += -reqSpin;
      if (a > 1) a = 1;
      if (a > 0 && thrusterPortBlocked(ship, m, eff.w, eff.h, -shipDir.x, -shipDir.y)) {
        a = 0;
      }
      _desiredActivation[i] = a;
      wantedW += port.powerW * a;
    }
    const budget = _variableBudgetByNet.get(net) ?? 0;
    const scale = wantedW > 0 ? Math.min(1, budget / wantedW) : 1;
    _variableBudgetByNet.set(net, Math.max(0, budget - wantedW * scale));
    for (let i = 0; i < tCount; i++) {
      const a = _desiredActivation[i] * scale;
      m.thrusterActivations[i] = a;
      if (a <= 0) continue;
      const port = def.thrusters[i];
      const shipDir = cardinalVec(rotateCardinal(port.dir, m.facing));
      const Fx = shipDir.x * port.thrustN;
      const Fy = shipDir.y * port.thrustN;
      const tau = r.x * Fy - r.y * Fx;
      totalFx += Fx * a;
      totalFy += Fy * a;
      totalTau += tau * a;
      _activeDrawByNet.set(net, (_activeDrawByNet.get(net) ?? 0) + port.powerW * a);
      _fuelBurnByNet.set(net, (_fuelBurnByNet.get(net) ?? 0) + port.fuelBurnKgPerSec * a);
      _activeHeatByModule[mi] += port.heatW * a;
    }
  }

  const rot = jitterAngle(ship.body.rot);
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  const worldFx = c * totalFx - s * totalFy;
  const worldFy = s * totalFx + c * totalFy;
  const invM = ship.body.mass > 0 ? 1 / ship.body.mass : 0;
  ship.body.vel.x += worldFx * invM * dt;
  ship.body.vel.y += worldFy * invM * dt;
  if (ship.body.inertia > 0) {
    ship.body.angVel += (totalTau / ship.body.inertia) * dt;
  }

  for (const net of nets.networks) {
    let reactorCap = 0;
    let drawW = 0;
    let batteryRoomWh = 0;
    for (const m of net.modules) {
      if (m.hp <= 0 || m.disabled) continue;
      const def = m.def;
      if (def.category === "reactor") reactorCap += def.stats.powerOutW;
      else drawW += def.stats.powerIdleW;
      if (m.energyWh != null) {
        const cap = def.stats.batteryWh;
        batteryRoomWh += Math.max(0, cap - m.energyWh);
      }
    }
    const activeDraw = _activeDrawByNet.get(net) ?? 0;
    const consumerW = drawW + activeDraw;
    const consumerWh = (consumerW * dt) / 3600;

    const drainedWh = drawEnergyFromNetwork(net, consumerWh);
    const deficitWh = consumerWh - drainedWh;

    const batteryCatchUpW = (batteryRoomWh * 3600) / BATTERY_FILL_TARGET_S;
    const reactorTargetW = consumerW + batteryCatchUpW;
    const producedW = Math.min(reactorCap, reactorTargetW);
    const loadFactor = reactorCap > 0 ? producedW / reactorCap : 0;

    for (const m of net.modules) {
      if (m.hp <= 0 || m.disabled) continue;
      const def = m.def;
      if (def.category !== "reactor") continue;
      m.loadFactor = loadFactor;
      if (def.reactor && def.reactor.fuelJoulesPerKg > 0) {
        const burnKg = (def.stats.powerOutW * loadFactor * dt)
          / def.reactor.fuelJoulesPerKg;
        if (burnKg > 0 && m.fuelBuffer != null && m.fuelBuffer >= burnKg * 0.999) {
          m.fuelBuffer -= burnKg;
          m.fuelStarved = false;
        } else if (burnKg > 0) {
          m.fuelStarved = true;
        } else {
          m.fuelStarved = false;
        }
      }
    }

    const producedWh = (producedW * dt) / 3600;
    const deficitCoveredWh = Math.min(producedWh, deficitWh);
    const leftoverWh = producedWh - deficitCoveredWh;
    const unhandledDeficitWh = deficitWh - deficitCoveredWh;
    if (leftoverWh > 0) {
      addEnergyToNetwork(net, leftoverWh);
    }

    if (unhandledDeficitWh > 1e-9) {
      for (const m of net.modules) {
        if (m.hp <= 0 || m.disabled) continue;
        const def = m.def;
        if (def.category === "reactor") continue;
        if (def.stats.powerIdleW <= 0 && def.stats.powerActiveW <= 0) continue;
        m.awaitingPower = true;
        m.rebootAt = undefined;
      }
    }
  }
  for (const [net, kgPerSec] of _fuelBurnByNet) {
    if (kgPerSec > 0) drawFuelFromNetwork(net, kgPerSec * dt);
  }

  for (const m of ship.modules) {
    if (m.hp <= 0 || m.disabled || m.manned === false) continue;
    const def = m.def;
    const w = def.weapon;
    if (!w || w.ammoMax == null || !w.ammoResource) continue;
    if (m.ammo == null) m.ammo = 0;
    if (m.ammo >= w.ammoMax) { m.ammoRefillS = 0; continue; }
    const cadence = w.ammoRefillS ?? DEFAULT_AMMO_REFILL_S;
    m.ammoRefillS = (m.ammoRefillS ?? 0) + dt;
    const net = nets.networkOf.get(m);
    if (!net) continue;
    while (m.ammoRefillS >= cadence && m.ammo < w.ammoMax) {
      const kg = w.projectileMass;
      const took = drawCargoFromNetwork(net, w.ammoResource, kg);
      if (took < kg * 0.999) break;
      m.ammo += 1;
      m.ammoRefillS -= cadence;
    }
  }

  if (!thermalActive || !runThermal) return;
  stepShipThermal(ship, HALF_RATE_DT, _activeHeatByModule, nets.heatPumpEdges, nets.conductionEdges);

  // Melt damage lives here to avoid thermal→damage→thermal cycle.
  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    if (m.tempK > m.def.stats.meltPointK) {
      damageModule(world, ship, m, m.hp + 1, RUINED_QUALITY, 0, dirty, "heat");
    }
  }
}

function fireWeapon(
  world: World,
  ship: Ship,
  m: ShipModule,
  cells: { w: number; h: number },
  w: NonNullable<ModuleDef["weapon"]>,
): void {
  const eff = m.facing % 2 === 0
    ? { w: cells.w, h: cells.h }
    : { w: cells.h, h: cells.w };
  const cellSize = shipCellSize(ship);
  const lx = (m.gx + eff.w / 2) * cellSize - ship.com.x;
  const ly = (m.gy + eff.h / 2) * cellSize - ship.com.y;
  const rot = jitterAngle(ship.body.rot);
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  const wx = ship.body.pos.x + lx * c - ly * s;
  const wy = ship.body.pos.y + lx * s + ly * c;

  const aim = jitterAngle(
    ship.body.rot + m.facing * (Math.PI / 2) + (Math.random() - 0.5) * w.spreadRad,
  );
  const fwdX = Math.cos(aim);
  const fwdY = Math.sin(aim);

  const offset = cellSize * Math.max(cells.w, cells.h) * 0.6;
  const density = w.densityKgPerM3 ?? 7_850;
  const volume = Math.max(1e-12, w.projectileMass) / density;
  const thermalRadiusM = Math.cbrt((3 * volume) / (4 * Math.PI));
  const proj: Projectile = {
    id: world.nextId++,
    pos: { x: wx + fwdX * offset, y: wy + fwdY * offset },
    vel: { x: ship.body.vel.x + fwdX * w.muzzleVel, y: ship.body.vel.y + fwdY * w.muzzleVel },
    mass: w.projectileMass,
    damage: w.damage,
    ttl: w.range / w.muzzleVel,
    ownerId: ship.id,
    size: w.projectileSize,
    thermalRadiusM,
    sprite: w.projectileSprite,
    tempK: 0,
    blastRadiusM: w.blastRadiusM,
    qualityMod: w.qualityMod,
    heatOnImpactJ: w.heatOnImpactJ,
    heatOnRicochetJ: w.heatOnRicochetJ,
    restitution: w.restitution,
    class: w.class,
    specificHeatJPerKgK: w.specificHeatJPerKgK ?? 500,
    emissivity: w.emissivity ?? 0.7,
    hardness: w.hardness ?? 0.6,
  };
  world.projectiles.set(proj.id, proj);

  if (ship.body.mass > 0) {
    const dv = (w.projectileMass * w.muzzleVel) / ship.body.mass;
    ship.body.vel.x -= fwdX * dv;
    ship.body.vel.y -= fwdY * dv;
  }
  if (ship.body.inertia > 0) {
    const imp = -w.projectileMass * w.muzzleVel;
    const rx = lx * c - ly * s;
    const ry = lx * s + ly * c;
    ship.body.angVel += (rx * (fwdY * imp) - ry * (fwdX * imp)) / ship.body.inertia;
  }
}

function fireBeam(
  world: World,
  ship: Ship,
  m: ShipModule,
  def: ModuleDef,
  w: NonNullable<ModuleDef["weapon"]>,
  dirty: Set<Ship>,
  shipGrid: ShipGrid,
): void {
  const eff = effectiveCells(def, m.facing);
  const ownCell = shipCellSize(ship);
  const lx = (m.gx + eff.w / 2) * ownCell - ship.com.x;
  const ly = (m.gy + eff.h / 2) * ownCell - ship.com.y;
  const muzzleRot = jitterAngle(ship.body.rot);
  const cR = Math.cos(muzzleRot);
  const sR = Math.sin(muzzleRot);
  const muzzleX = ship.body.pos.x + lx * cR - ly * sR;
  const muzzleY = ship.body.pos.y + lx * sR + ly * cR;

  const facingAng = m.facing * (Math.PI / 2);
  const aimAng = jitterAngle(
    ship.body.rot + facingAng + (Math.random() - 0.5) * w.spreadRad,
  );
  const dirX = Math.cos(aimAng);
  const dirY = Math.sin(aimAng);
  const perpX = -dirY;
  const perpY = dirX;

  // Skip past the firing module so the beam never self-hits.
  const muzzleClear = ownCell * Math.max(eff.w, eff.h) * 0.6;

  const beamWidthM = w.beamWidthM ?? 0.5;
  const rayCount = Math.max(1, Math.floor(w.beamRayCount ?? 1));
  const rayWidthM = beamWidthM / rayCount;
  const beamSprite = w.beamSprite ?? "mining_beam";
  const beamWattage = def.stats.powerActiveW * def.stats.energyEfficiency;
  const depositJPerRay = (beamWattage * TICK_DT) / rayCount;

  // Grids store entities under every cell they cover; dedupe per ray.
  const visitedShips = _beamVisitedShips;
  const visitedModules = _beamVisitedModules;

  for (let ri = 0; ri < rayCount; ri++) {
    const lateral = rayCount === 1
      ? 0
      : ((ri + 0.5) / rayCount - 0.5) * beamWidthM;
    const originX = muzzleX + perpX * lateral;
    const originY = muzzleY + perpY * lateral;
    const startX = originX + dirX * muzzleClear;
    const startY = originY + dirY * muzzleClear;

    // Holder object: TS narrows a plain `let best: Ship | null = null` to the
    // literal `null` because the only assignments happen inside callbacks.
    const best: { t: number; ship: Ship | null; module: ShipModule | null } = {
      t: w.range, ship: null, module: null,
    };

    visitedShips.clear();
    forEachShipAlongRay(shipGrid, startX, startY, dirX, dirY, best.t, (other, cellT) => {
      if (cellT >= best.t) return false;
      if (other.id === ship.id) return;
      if (visitedShips.has(other)) return;
      visitedShips.add(other);

      const extent = shipExtent(other);
      const tCirc = rayCircleEntry(
        startX, startY, dirX, dirY,
        other.body.pos.x, other.body.pos.y, extent,
      );
      if (tCirc >= best.t) return;

      const dx0 = startX - other.body.pos.x;
      const dy0 = startY - other.body.pos.y;
      const otherRot = jitterAngle(-other.body.rot);
      const cs = Math.cos(otherRot);
      const sn = Math.sin(otherRot);
      const localOX = dx0 * cs - dy0 * sn + other.com.x;
      const localOY = dx0 * sn + dy0 * cs + other.com.y;
      const localDX = dirX * cs - dirY * sn;
      const localDY = dirX * sn + dirY * cs;
      const otherCell = shipCellSize(other);

      const moduleByCell = getNetworks(other).moduleByCell;
      if (moduleByCell.size === 0) return;

      visitedModules.clear();
      forEachCellAlongLocalRay(localOX, localOY, localDX, localDY, otherCell, best.t, (gx, gy, cellEntryT) => {
        if (cellEntryT >= best.t) return false;
        const om = moduleByCell.get(cellKeyNum(gx, gy));
        if (!om || visitedModules.has(om)) return;
        visitedModules.add(om);
        const od = om.def;
        const oeff = effectiveCells(od, om.facing);
        const x0 = om.gx * otherCell;
        const y0 = om.gy * otherCell;
        const x1 = x0 + oeff.w * otherCell;
        const y1 = y0 + oeff.h * otherCell;
        const t = rayAabbEntry(localOX, localOY, localDX, localDY, x0, y0, x1, y1);
        if (t < 0 || t >= best.t) return;
        best.t = t;
        best.ship = other;
        best.module = om;
      });
    });

    const bestT = best.t;
    const bestShip = best.ship;
    const bestModule = best.module;

    if (bestShip && bestModule) {
      damageModule(world, bestShip, bestModule, 0,
                   w.qualityMod, depositJPerRay, dirty, "beam");
      // Ablate with beam qualityMod so mined ore keeps quality (generic
      // melt path would ruin it).
      const failLimit = bestModule.def.stats.meltPointK;
      if (bestModule.hp > 0 && bestModule.tempK > failLimit) {
        damageModule(world, bestShip, bestModule, bestModule.hp,
                     w.qualityMod, 0, dirty, "beam");
      }
      const hitX = startX + dirX * bestT;
      const hitY = startY + dirY * bestT;
      const bestRot = jitterAngle(-bestShip.body.rot);
      const bcos = Math.cos(bestRot);
      const bsin = Math.sin(bestRot);
      const bdx = hitX - bestShip.body.pos.x;
      const bdy = hitY - bestShip.body.pos.y;
      const lhx = bdx * bcos - bdy * bsin + bestShip.com.x;
      const lhy = bdx * bsin + bdy * bcos + bestShip.com.y;
      const oeff = effectiveCells(bestModule.def, bestModule.facing);
      const obc = shipCellSize(bestShip);
      const ox0 = bestModule.gx * obc;
      const oy0 = bestModule.gy * obc;
      const ox1 = ox0 + oeff.w * obc;
      const oy1 = oy0 + oeff.h * obc;
      const dL = Math.abs(lhx - ox0);
      const dR = Math.abs(ox1 - lhx);
      const dT = Math.abs(lhy - oy0);
      const dB = Math.abs(oy1 - lhy);
      const minD = Math.min(dL, dR, dT, dB);
      const fdx = minD === dL ? -1 : minD === dR ? 1 : 0;
      const fdy = minD === dT ? -1 : minD === dB ? 1 : 0;
      emitSparks(world, bestShip, bestModule, hitX, hitY, fdx, fdy,
                 depositJPerRay, undefined, undefined, ETA_BEAM);
    }

    world.beams.push({
      fromX: originX, fromY: originY,
      toX: startX + dirX * bestT, toY: startY + dirY * bestT,
      widthM: rayWidthM,
      sprite: beamSprite,
      vx: ship.body.vel.x,
      vy: ship.body.vel.y,
    });
  }
}

function rayCircleEntry(
  ox: number, oy: number, dx: number, dy: number,
  cx: number, cy: number, r: number,
): number {
  const mx = ox - cx;
  const my = oy - cy;
  const b = mx * dx + my * dy;
  const c = mx * mx + my * my - r * r;
  if (c > 0 && b > 0) return Infinity;
  const disc = b * b - c;
  if (disc < 0) return Infinity;
  const t = -b - Math.sqrt(disc);
  return t < 0 ? 0 : t;
}

function rayAabbEntry(
  ox: number, oy: number, dx: number, dy: number,
  x0: number, y0: number, x1: number, y1: number,
): number {
  let tmin = -Infinity;
  let tmax = Infinity;
  if (Math.abs(dx) < 1e-12) {
    if (ox < x0 || ox > x1) return -1;
  } else {
    const inv = 1 / dx;
    let t0 = (x0 - ox) * inv;
    let t1 = (x1 - ox) * inv;
    if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; }
    if (t0 > tmin) tmin = t0;
    if (t1 < tmax) tmax = t1;
    if (tmin > tmax) return -1;
  }
  if (Math.abs(dy) < 1e-12) {
    if (oy < y0 || oy > y1) return -1;
  } else {
    const inv = 1 / dy;
    let t0 = (y0 - oy) * inv;
    let t1 = (y1 - oy) * inv;
    if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; }
    if (t0 > tmin) tmin = t0;
    if (t1 < tmax) tmax = t1;
    if (tmin > tmax) return -1;
  }
  if (tmax < 0) return -1;
  return tmin < 0 ? 0 : tmin;
}

export function damageModule(
  world: World,
  ship: Ship,
  m: ShipModule,
  dmg: number,
  qualityMod: number,
  heatJ: number,
  dirty: Set<Ship>,
  cause: DamageCause,
): void {
  if (m.hp <= 0) return;
  // A damaged asteroid can no longer be regenerated from its seed, so pin it as
  // a full Ship (it will never dehydrate back to a proxy).
  if (ship.isAsteroid) ship.modified = true;
  if (heatJ > 0) m.tempK += heatJ / thermalCap(m.def);
  if (dmg <= 0) return;
  m.hp -= dmg;
  if (m.hp <= 0) {
    m.hp = 0;
    const def = m.def;
    if (def.mineable && ship.isAsteroid) {
      spawnRockDebris(world, ship, m, def, clamp(m.quality * qualityMod, 0, 1));
    }
    world.events.push({
      kind: "module-destroyed",
      t: world.time,
      shipId: ship.id,
      shipName: ship.name,
      shipIsAsteroid: ship.isAsteroid,
      defId: m.defId,
      defName: def.name,
      cause,
    });
    dirty.add(ship);
    ship.collider = null;
    ship._extent = null;
  }
}

export function applyBlastDamage(
  world: World,
  ship: Ship,
  hitX: number, hitY: number,
  radius: number,
  damage: number,
  qualityMod: number,
  heatOnImpactJ: number,
  skip: ShipModule,
  dirty: Set<Ship>,
): void {
  const r2 = radius * radius;
  for (const m of ship.modules) {
    if (m === skip || m.hp <= 0) continue;
    const mc = moduleWorldCenter(ship, m);
    const dx = mc.x - hitX;
    const dy = mc.y - hitY;
    const d2 = dx * dx + dy * dy;
    if (d2 > r2) continue;
    const falloff = 1 - Math.sqrt(d2) / radius;
    damageModule(world, ship, m, damage * falloff, qualityMod, heatOnImpactJ * falloff, dirty, "bullet");
  }
}

export function cleanupShipAfterDamage(world: World, ship: Ship): void {
  const mods = ship.modules;
  // markModulesDirty needs dead-module refs while still cached; collect
  // before the compaction below removes them.
  let deadModules: ShipModule[] | null = null;
  for (let i = 0; i < mods.length; i++) {
    if (mods[i].hp <= 0) {
      if (!deadModules) deadModules = [];
      deadModules.push(mods[i]);
    }
  }

  let write = 0;
  for (let read = 0; read < mods.length; read++) {
    if (mods[read].hp > 0) {
      if (write !== read) mods[write] = mods[read];
      write++;
    }
  }
  const removed = mods.length - write;
  mods.length = write;

  if (mods.length === 0) {
    world.ships.delete(ship.id);
    return;
  }
  if (removed === 0) return;
  if (deadModules) markModulesDirty(ship, deadModules);
  else markNetworksDirty(ship);

  recomputeShipMassAndRebase(ship);
  splitDisconnectedComponents(world, ship);
}

function spawnRockDebris(
  world: World,
  ship: Ship,
  m: ShipModule,
  def: ModuleDef,
  quality: number,
): void {
  if (!def.mineable) return;
  const eff = effectiveCells(def, m.facing);
  const cell = shipCellSize(ship);
  const rot = jitterAngle(ship.body.rot);
  const cs = Math.cos(rot);
  const sn = Math.sin(rot);

  for (let qi = 0; qi < 4; qi++) {
    const qx = qi & 1 ? 0.25 : -0.25;
    const qy = qi & 2 ? 0.25 : -0.25;
    const lx = (m.gx + eff.w / 2 + qx * eff.w) * cell - ship.com.x;
    const ly = (m.gy + eff.h / 2 + qy * eff.h) * cell - ship.com.y;
    const wx = lx * cs - ly * sn;
    const wy = lx * sn + ly * cs;
    const rLen = Math.hypot(wx, wy) || 1;
    const pushSpeed = 0.5 + Math.random() * 1.0;
    spawnItem(world, def.mineable.debrisItemId, {
      x: ship.body.pos.x + wx,
      y: ship.body.pos.y + wy,
      vx: ship.body.vel.x + (wx / rLen) * pushSpeed,
      vy: ship.body.vel.y + (wy / rLen) * pushSpeed,
      rot: ship.body.rot,
      angVel: ship.body.angVel + (Math.random() - 0.5) * 0.6,
      quality,
      tempK: m.tempK,
    });
  }
}

export { totalCargoCapacity, totalCargoMass } from "./ship";
export { shipCargoBreakdown, shipCargoQuality as cargoQuality } from "./ship";
